import { useState } from 'react';
import { FilterField } from '../../components/ui/data-table';
import PageHeader from '../../components/common/PageHeader';
import StatsCard from '../../components/common/StatsCard';
import CreateReferralLinkForm, {
  ReferralLinkData,
} from '../../components/ib/CreateReferralLinkForm';
import BannerLibrary, { Banner } from '../../components/ib/BannerLibrary';
import ConversionFunnel from '../../components/ib/ConversionFunnel';
import MarketingCampaigns, {
  MarketingTool,
} from '../../components/ib/MarketingCampaigns';
import { Link, Image, Target, TrendingUp } from 'lucide-react';

const IBMarketingTools = () => {
  const [searchValue, setSearchValue] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: string }>(
    {}
  );
  const [showReferralModal, setShowReferralModal] = useState(false);

  const handleReferralLinkSubmit = (data: ReferralLinkData) => {
    console.log('Referral link data:', data);
  };

  const marketingTools: MarketingTool[] = [
    {
      id: '1',
      type: 'referral-link',
      name: 'Main Referral Link',
      referralUrl: 'https://example.com/ref/john',
      landingPage: 'https://example.com',
      clicks: 5420,
      registrations: 324,
      deposits: '$48,600',
      conversionRate: '6.0%',
      status: 'active',
      createdDate: '2024-01-15',
    },
    {
      id: '2',
      type: 'promo-banner',
      name: 'Spring Campaign Banner',
      referralUrl: 'https://example.com/banner/spring',
      landingPage: 'https://example.com/spring',
      clicks: 2890,
      registrations: 156,
      deposits: '$23,400',
      conversionRate: '5.4%',
      status: 'active',
      createdDate: '2024-03-01',
    },
    {
      id: '3',
      type: 'campaign',
      name: 'Q1 Trading Contest',
      referralUrl: 'https://example.com/campaign/q1',
      landingPage: 'https://example.com/contest',
      clicks: 4120,
      registrations: 287,
      deposits: '$57,400',
      conversionRate: '7.0%',
      status: 'active',
      createdDate: '2024-02-01',
    },
    {
      id: '4',
      type: 'utm-link',
      name: 'Email Campaign Link',
      referralUrl: 'https://example.com/utm/email',
      landingPage: 'https://example.com',
      clicks: 1850,
      registrations: 98,
      deposits: '$12,500',
      conversionRate: '5.3%',
      status: 'active',
      createdDate: '2024-03-10',
    },
    {
      id: '5',
      type: 'referral-link',
      name: 'Social Media Link',
      referralUrl: 'https://example.com/ref/social',
      landingPage: 'https://example.com',
      clicks: 3240,
      registrations: 189,
      deposits: '$28,350',
      conversionRate: '5.8%',
      status: 'active',
      createdDate: '2024-02-15',
    },
    {
      id: '6',
      type: 'promo-banner',
      name: 'Summer Promo Banner',
      referralUrl: 'https://example.com/banner/summer',
      landingPage: 'https://example.com/summer',
      clicks: 1200,
      registrations: 45,
      deposits: '$4,500',
      conversionRate: '3.8%',
      status: 'inactive',
      createdDate: '2023-12-01',
    },
    {
      id: '7',
      type: 'campaign',
      name: 'New Year Bonus Campaign',
      referralUrl: 'https://example.com/campaign/newyear',
      landingPage: 'https://example.com/bonus',
      clicks: 5670,
      registrations: 412,
      deposits: '$82,400',
      conversionRate: '7.3%',
      status: 'inactive',
      createdDate: '2024-01-01',
    },
    {
      id: '8',
      type: 'utm-link',
      name: 'Google Ads Link',
      referralUrl: 'https://example.com/utm/google',
      landingPage: 'https://example.com',
      clicks: 0,
      registrations: 0,
      deposits: '$0',
      conversionRate: '0.0%',
      status: 'pending',
      createdDate: '2024-04-01',
    },
  ];

  const banners: Banner[] = [
    {
      id: '1',
      name: 'Spring Promo Banner',
      size: '728x90',
      image:
        'https://placehold.co/728x90/3b82f6/ffffff?text=Spring+Promo+Banner',
      clicks: 1234,
    },
    {
      id: '2',
      name: 'Sidebar Banner',
      size: '300x250',
      image: 'https://placehold.co/300x250/10b981/ffffff?text=Sidebar+Banner',
      clicks: 567,
    },
    {
      id: '3',
      name: 'Skyscraper Banner',
      size: '160x600',
      image: 'https://placehold.co/160x600/f59e0b/ffffff?text=Skyscraper',
      clicks: 890,
    },
    {
      id: '4',
      name: 'Trading Contest Banner',
      size: '728x90',
      image: 'https://placehold.co/728x90/8b5cf6/ffffff?text=Trading+Contest',
      clicks: 2341,
    },
    {
      id: '5',
      name: 'Welcome Bonus Banner',
      size: '300x250',
      image: 'https://placehold.co/300x250/ec4899/ffffff?text=Welcome+Bonus',
      clicks: 1890,
    },
    {
      id: '6',
      name: 'Mobile App Banner',
      size: '320x50',
      image: 'https://placehold.co/320x50/06b6d4/ffffff?text=Mobile+App',
      clicks: 3456,
    },
  ];

  const funnelData = {
    clicks: marketingTools.reduce((sum, tool) => sum + tool.clicks, 0),
    registrations: marketingTools.reduce(
      (sum, tool) => sum + tool.registrations,
      0
    ),
    deposits: marketingTools.reduce((sum, tool) => sum + tool.registrations, 0),
  };

  const filterFields: FilterField[] = [
    {
      id: 'type',
      label: 'Type',
      options: [
        { value: 'referral-link', label: 'Referral Link' },
        { value: 'promo-banner', label: 'Promo Banner' },
        { value: 'campaign', label: 'Campaign' },
        { value: 'utm-link', label: 'UTM Link' },
      ],
    },
    {
      id: 'status',
      label: 'Status',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'pending', label: 'Pending' },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Marketing Tools"
          description="Manage referral links, promo banners, and campaign tracking"
          actionLabel="Create Referral Link"
          actionIcon={Link}
          onAction={() => setShowReferralModal(true)}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard
          title="Total Referral Links"
          value={marketingTools
            .filter(t => t.type === 'referral-link')
            .length.toString()}
          change="2 active links"
          icon={Link}
        />
        <StatsCard
          title="Total Clicks"
          value={marketingTools
            .reduce((sum, t) => sum + t.clicks, 0)
            .toLocaleString()}
          change="+18.5% from last month"
          icon={Target}
        />
        <StatsCard
          title="Conversions"
          value={marketingTools
            .reduce((sum, t) => sum + t.registrations, 0)
            .toLocaleString()}
          change="6.0% conversion rate"
          icon={TrendingUp}
        />
        <StatsCard
          title="Active Campaigns"
          value={marketingTools
            .filter(t => t.status === 'active')
            .length.toString()}
          change="5 of 8 active"
          icon={Image}
        />
      </div>
      <MarketingCampaigns
        data={marketingTools}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onSearchReset={() => setSearchValue('')}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        filterFields={filterFields}
        activeFilters={activeFilters}
        onFilterChange={setActiveFilters}
        onFiltersClear={() => setActiveFilters({})}
        onFiltersApply={() => {}}
        onExport={() => console.log('Export campaigns')}
      />
      <BannerLibrary
        banners={banners}
        onExport={() => console.log('Export banners')}
      />
      <ConversionFunnel data={funnelData} />
      <CreateReferralLinkForm
        isOpen={showReferralModal}
        onClose={() => setShowReferralModal(false)}
        onSubmit={handleReferralLinkSubmit}
      />
    </div>
  );
};

export default IBMarketingTools;
